// src/pages/supervisor/AssignedStudents.js

import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    Box,
    Grid,
    Card,
    CardContent,
    Avatar,
    Chip,
    LinearProgress,
    useTheme,
    Tooltip,
    Alert,
    CircularProgress,
    Fade,
    Zoom,
    Divider,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Badge,
    CardActions,
    Stack,
    useMediaQuery,
    Tab,
    Tabs
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Person,
    Email,
    Phone,
    CheckCircle,
    Warning,
    Schedule,
    TrendingUp,
    School,
    Verified,
    Pending,
    CalendarToday,
    Feedback,
    People,
    Flag,
    PriorityHigh,
    VideoCall,
    Edit,
    Send,
    Visibility,
    Close,
    Assignment,
    Chat,
    DoneAll,
    Done
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import useCustomToast from '../../hooks/useToast';
import {
    APIStudentProfileImage,
    APIAssignStudentBySupervisor,
    APIAssignStudentStatsBySupervisor,
    APIAssignStudentUpdateStatus,
    APIAssignStudentMessages,
    APIAssignStudentSendMessage,
    APIAssignStudentMarkRead,
    APIAssignStudentUnreadCount
} from '../../apiServices/APIs';
import { 
    formatCreatedAtDate, 
    getUserDetails,
    getImageUrl
} from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';
import SupervisorBar from '../../components/SupervisorBar';
import { FullWidthContainer } from '../../components/FullWidthContainer';

// Helper function to get initials from user name
const getInitials = (name) => {
    if (!name) return 'ST';
    return name
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
};

// Helper function to get avatar color based on user name
const stringToColor = (string) => {
    if (!string) return '#1976d2';
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xFF;
        color += ('00' + value.toString(16)).substr(-2);
    }
    return color;
};

const AssignedStudents = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [assignments, setAssignments] = useState([]);
    const [stats, setStats] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [openStatusDialog, setOpenStatusDialog] = useState(false);
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [openChatDialog, setOpenChatDialog] = useState(false);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [sendingMessage, setSendingMessage] = useState(false);
    const [unreadCounts, setUnreadCounts] = useState({});
    const [statusForm, setStatusForm] = useState({ status: '', notes: '' });
    const [actionLoading, setActionLoading] = useState(false);
    const showToast = useCustomToast();
    const isMounted = useRef(true);
    const supervisorId = getUserDetails("Id");

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchAllData();
        
        // Auto-refresh every 2 minutes
        const interval = setInterval(() => {
            fetchAllData(true);
        }, 120000);
        
        return () => {
            isMounted.current = false;
            clearInterval(interval);
        };
    }, []);

    const fetchAllData = async (silent = false) => {
        if (!silent) setLoading(true);
        
        try {
            await Promise.all([
                fetchAssignments(),
                fetchStats()
            ]);
        } catch (error) {
            if (!silent) {
                setError("Failed to load data");
                console.error("Fetch error:", error);
            }
        } finally {
            if (!silent && isMounted.current) {
                setLoading(false);
            }
        }
    };

    const fetchAssignments = async () => {
        try {
            const result = await apiService.listById(APIAssignStudentBySupervisor, supervisorId);
            if (result.success && isMounted.current) {
                setAssignments(Array.isArray(result.data) ? result.data : []);
                // Fetch unread counts for each assignment
                await fetchUnreadCounts(Array.isArray(result.data) ? result.data : []);
            } else {
                console.error("Failed to fetch assignments:", result?.message);
            }
        } catch (error) {
            console.error("Error fetching assignments:", error);
        }
    };

    const fetchStats = async () => {
        try {
            const result = await apiService.listById(APIAssignStudentStatsBySupervisor, supervisorId);
            if (result.success && result.data && isMounted.current) {
                setStats(result.data);
            }
        } catch (error) {
            console.error("Error fetching stats:", error);
        }
    };

    const fetchUnreadCounts = async (assignmentsList) => {
        try {
            const result = await apiService.listById(APIAssignStudentUnreadCount, `${supervisorId}/supervisor`);
            if (result.success && result.data) {
                setUnreadCounts({ [selectedAssignment?.id]: result.data.unread_count });
            }
        } catch (error) {
            console.error("Error fetching unread counts:", error);
        }
    };

    const fetchMessages = async (assignmentId) => {
        try {
            const result = await apiService.listById(APIAssignStudentMessages, assignmentId, {
                user_id: supervisorId,
                user_type: 'supervisor'
            });
            if (result.success && isMounted.current) {
                setMessages(Array.isArray(result.data) ? result.data : []);
                // Mark as read
                const markFormData = new FormData();
                markFormData.append('assignment_id', assignmentId);
                markFormData.append('user_id', supervisorId);
                markFormData.append('user_type', 'supervisor');
                await apiService.update(APIAssignStudentMarkRead, markFormData);
                // Update unread count
                fetchUnreadCounts(assignments);
            }
        } catch (error) {
            console.error("Error fetching messages:", error);
        }
    };

    const handleUpdateStatus = async () => {
        if (!selectedAssignment) return;
        
        setActionLoading(true);
        try {
            const formData = new FormData();
            formData.append('updateId', selectedAssignment.id);
            formData.append('status', statusForm.status);
            if (statusForm.notes) formData.append('assignment_notes', statusForm.notes);
            
            const result = await apiService.update(APIAssignStudentUpdateStatus, formData);
            
            if (result && result.success) {
                showToast("Success", "Assignment status updated", "success");
                fetchAllData();
                handleCloseStatusDialog();
            } else {
                showToast("Error", result?.message || "Failed to update status", "error");
            }
        } catch (error) {
            console.error("Update status error:", error);
            showToast("Error", error.response?.data?.message || "Failed to update status", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleSendMessage = async () => {
        if (!newMessage.trim() || !selectedAssignment) return;
        
        setSendingMessage(true);
        try {
            const formData = new FormData();
            formData.append('assignment_id', selectedAssignment.id);
            formData.append('sender_id', supervisorId);
            formData.append('sender_type', 'supervisor');
            formData.append('receiver_id', selectedAssignment.student.id);
            formData.append('receiver_type', 'student');
            formData.append('message', newMessage);
            
            const result = await apiService.create(APIAssignStudentSendMessage, formData);
            
            if (result && result.success) {
                setMessages(prev => [...prev, result.data]);
                setNewMessage('');
                // Scroll to bottom
                setTimeout(() => {
                    const chatContainer = document.getElementById('chat-messages');
                    if (chatContainer) {
                        chatContainer.scrollTop = chatContainer.scrollHeight;
                    }
                }, 100);
            } else {
                showToast("Error", result?.message || "Failed to send message", "error");
            }
        } catch (error) {
            console.error("Send message error:", error);
            showToast("Error", error.response?.data?.message || "Failed to send message", "error");
        } finally {
            setSendingMessage(false);
        }
    };

    const handleOpenStatusDialog = (assignment) => {
        setSelectedAssignment(assignment);
        setStatusForm({ status: assignment.status || 'active', notes: assignment.assignment_notes || '' });
        setOpenStatusDialog(true);
    };

    const handleCloseStatusDialog = () => {
        setOpenStatusDialog(false);
        setSelectedAssignment(null);
        setStatusForm({ status: '', notes: '' });
    };

    const handleOpenViewDialog = (assignment) => {
        setSelectedAssignment(assignment);
        setOpenViewDialog(true);
    };

    const handleCloseViewDialog = () => {
        setOpenViewDialog(false);
        setSelectedAssignment(null);
    };

    const handleOpenChatDialog = async (assignment) => {
        setSelectedAssignment(assignment);
        setOpenChatDialog(true);
        await fetchMessages(assignment.id);
    };

    const handleCloseChatDialog = () => {
        setOpenChatDialog(false);
        setSelectedAssignment(null);
        setMessages([]);
        setNewMessage('');
    };

    const getProfileImageUrl = (student) => {
        if (student?.profile_image) {
            return getImageUrl(APIStudentProfileImage, student.profile_image);
        }
        return null;
    };

    const getStatusColor = (status) => {
        switch(status) {
            case 'active': return 'success';
            case 'completed': return 'info';
            case 'pending': return 'warning';
            case 'cancelled': return 'error';
            default: return 'default';
        }
    };

    const getStatusLabel = (status) => {
        switch(status) {
            case 'active': return 'Active';
            case 'completed': return 'Completed';
            case 'pending': return 'Pending';
            case 'cancelled': return 'Cancelled';
            default: return status || 'Active';
        }
    };

    const filteredAssignments = () => {
        if (activeTab === 0) return assignments;
        if (activeTab === 1) return assignments.filter(a => a.status === 'active');
        if (activeTab === 2) return assignments.filter(a => a.status === 'pending');
        if (activeTab === 3) return assignments.filter(a => a.status === 'completed');
        return assignments;
    };

    if (loading) {
        return <LoadingState message="Loading Assigned Students..." variant="research" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchAllData} title="Unable to Load Assigned Students" variant="research" fullPage retryText="Retry Loading" />;
    }

    const displayAssignments = filteredAssignments();

    return (
        <>
            <FullWidthContainer>
                <Box sx={{ backgroundColor: 'white', borderRadius: 3 }}>
                    <SupervisorBar notificationCount={5} />
                    
                    {/* Header Section */}
                    <Paper elevation={2} sx={{ 
                        p: { xs: 2, sm: 3 }, 
                        mb: { xs: 2, sm: 3 }, 
                        borderRadius: 3,
                        backgroundColor: 'white',
                    }}>
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            flexWrap: 'wrap', 
                            gap: 2,
                            flexDirection: { xs: 'column', sm: 'row' }
                        }}>
                            <Typography variant="h6" sx={{ 
                                fontWeight: 'bold', 
                                color: theme.palette.primary.main, 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                fontSize: { xs: '1rem', sm: '1.25rem' }
                            }}>
                                <People /> Assigned Students ({assignments.length})
                            </Typography>
                            <Button 
                                variant="outlined" 
                                color="info" 
                                startIcon={<RefreshIcon />} 
                                onClick={() => fetchAllData()} 
                                sx={{ 
                                    textTransform: 'none', 
                                    borderRadius: '8px',
                                    width: { xs: '100%', sm: 'auto' }
                                }}
                            >
                                Refresh
                            </Button>
                        </Box>

                        {/* Stats Cards */}
                        {stats && (
                            <Grid container spacing={2} sx={{ mt: 2 }}>
                                <Grid item xs={6} sm={3}>
                                    <Card sx={{ 
                                        textAlign: 'center', 
                                        bgcolor: theme.palette.info.light + '10',
                                        transition: 'transform 0.2s',
                                        '&:hover': { transform: 'translateY(-4px)' }
                                    }}>
                                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                            <Typography variant="h4" sx={{ 
                                                fontWeight: 'bold',
                                                fontSize: { xs: '1.5rem', sm: '2rem' }
                                            }}>
                                                {stats.total_assignments || 0}
                                            </Typography>
                                            <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                Total Students
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Card sx={{ 
                                        textAlign: 'center', 
                                        bgcolor: theme.palette.success.light + '10',
                                        transition: 'transform 0.2s',
                                        '&:hover': { transform: 'translateY(-4px)' }
                                    }}>
                                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                            <Typography variant="h4" sx={{ 
                                                fontWeight: 'bold',
                                                fontSize: { xs: '1.5rem', sm: '2rem' }
                                            }}>
                                                {stats.active_assignments || 0}
                                            </Typography>
                                            <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                Active
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Card sx={{ 
                                        textAlign: 'center', 
                                        bgcolor: theme.palette.warning.light + '10',
                                        transition: 'transform 0.2s',
                                        '&:hover': { transform: 'translateY(-4px)' }
                                    }}>
                                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                            <Typography variant="h4" sx={{ 
                                                fontWeight: 'bold',
                                                fontSize: { xs: '1.5rem', sm: '2rem' }
                                            }}>
                                                {stats.pending_assignments || 0}
                                            </Typography>
                                            <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                Pending
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Card sx={{ 
                                        textAlign: 'center', 
                                        bgcolor: theme.palette.error.light + '10',
                                        transition: 'transform 0.2s',
                                        '&:hover': { transform: 'translateY(-4px)' }
                                    }}>
                                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                            <Typography variant="h4" sx={{ 
                                                fontWeight: 'bold',
                                                fontSize: { xs: '1.5rem', sm: '2rem' }
                                            }}>
                                                {stats.completed_assignments || 0}
                                            </Typography>
                                            <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                Completed
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        )}
                    </Paper>
                </Box>
                
                {/* Tabs */}
                <Paper elevation={2} sx={{ 
                    borderRadius: 3, 
                    overflow: 'hidden',
                    backgroundColor: 'white',
                    mb: { xs: 2, sm: 3 }
                }}>
                    <Tabs
                        value={activeTab}
                        onChange={(e, v) => setActiveTab(v)}
                        sx={{ 
                            borderBottom: 1, 
                            borderColor: 'divider', 
                            px: { xs: 1, sm: 2 }, 
                            pt: 2,
                            '& .MuiTab-root': {
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                minWidth: { xs: 'auto', sm: 120 },
                                px: { xs: 1, sm: 2 }
                            }
                        }}
                        variant={isMobile ? "scrollable" : "standard"}
                        scrollButtons={isMobile ? "auto" : false}
                    >
                        <Tab label="All Students" />
                        <Tab label="Active" />
                        <Tab label="Pending" />
                        <Tab label="Completed" />
                    </Tabs>
                </Paper>

                {/* Students Grid */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                    {displayAssignments.length === 0 ? (
                        <Paper sx={{ 
                            p: { xs: 4, sm: 8 }, 
                            textAlign: 'center', 
                            borderRadius: 3,
                            backgroundColor: 'white'
                        }}>
                            <People sx={{ fontSize: { xs: 48, sm: 60 }, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                No assigned students
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Students will appear here once assigned
                            </Typography>
                        </Paper>
                    ) : (
                        <Grid container spacing={2}>
                            {displayAssignments.map((assignment, index) => {
                                const avatarColor = stringToColor(assignment.student?.name);
                                const initials = getInitials(assignment.student?.name);
                                const profileImage = getProfileImageUrl(assignment.student);
                                
                                return (
                                    <Grid item xs={12} sm={6} lg={4} key={assignment.id}>
                                        <Zoom in={true} style={{ transitionDelay: `${index * 50}ms` }}>
                                            <Card sx={{ 
                                                height: '100%', 
                                                display: 'flex', 
                                                flexDirection: 'column',
                                                transition: 'transform 0.2s, box-shadow 0.2s',
                                                '&:hover': {
                                                    transform: 'translateY(-4px)',
                                                    boxShadow: theme.shadows[8]
                                                }
                                            }}>
                                                <CardContent sx={{ flexGrow: 1, p: { xs: 2, sm: 3 } }}>
                                                    {/* Header */}
                                                    <Box sx={{ 
                                                        display: 'flex', 
                                                        justifyContent: 'space-between', 
                                                        alignItems: 'flex-start', 
                                                        mb: 2,
                                                        flexDirection: { xs: 'column', sm: 'row' },
                                                        gap: { xs: 1, sm: 0 }
                                                    }}>
                                                        <Box sx={{ 
                                                            display: 'flex', 
                                                            alignItems: 'center', 
                                                            gap: 2,
                                                            flexDirection: { xs: 'column', sm: 'row' },
                                                            textAlign: { xs: 'center', sm: 'left' }
                                                        }}>
                                                            <Avatar 
                                                                src={profileImage}
                                                                sx={{ 
                                                                    width: { xs: 48, sm: 56 }, 
                                                                    height: { xs: 48, sm: 56 },
                                                                    bgcolor: !profileImage ? avatarColor : 'transparent',
                                                                    fontWeight: 'bold'
                                                                }}
                                                            >
                                                                {!profileImage && initials}
                                                            </Avatar>
                                                            <Box>
                                                                <Typography variant="subtitle1" sx={{ 
                                                                    fontWeight: 'bold',
                                                                    fontSize: { xs: '0.9rem', sm: '1rem' }
                                                                }}>
                                                                    {assignment.student?.name}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                                    {assignment.student?.registration_id}
                                                                </Typography>
                                                                {assignment.student?.email && (
                                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                                                        <Email sx={{ fontSize: 12 }} />
                                                                        {assignment.student?.email}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                        <Chip 
                                                            label={getStatusLabel(assignment.status)} 
                                                            size="small" 
                                                            color={getStatusColor(assignment.status)}
                                                        />
                                                    </Box>

                                                    {/* Student Details */}
                                                    <Box sx={{ mb: 2 }}>
                                                        <Stack spacing={0.5}>
                                                            {assignment.student?.contact_phone && (
                                                                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    <Phone sx={{ fontSize: 12 }} />
                                                                    {assignment.student?.contact_phone}
                                                                </Typography>
                                                            )}
                                                            {assignment.student?.nationality && (
                                                                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    <Flag sx={{ fontSize: 12 }} />
                                                                    {assignment.student?.nationality}
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                    </Box>

                                                    {/* Assignment Info */}
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Assignment sx={{ fontSize: 12 }} />
                                                            ID: {assignment.assignment_id}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                                            <CalendarToday sx={{ fontSize: 12 }} />
                                                            Assigned: {formatCreatedAtDate(assignment.assigned_date)}
                                                        </Typography>
                                                    </Box>

                                                    {/* Assignment Notes */}
                                                    {assignment.assignment_notes && (
                                                        <Paper sx={{ p: 1, bgcolor: theme.palette.grey[50], borderRadius: 1, mb: 2 }}>
                                                            <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                                <strong>Notes:</strong> {assignment.assignment_notes}
                                                            </Typography>
                                                        </Paper>
                                                    )}
                                                </CardContent>

                                                <CardActions sx={{ 
                                                    p: 2, 
                                                    pt: 0, 
                                                    gap: 1, 
                                                    flexWrap: 'wrap',
                                                    justifyContent: { xs: 'center', sm: 'flex-start' }
                                                }}>
                                                    <Tooltip title="Chat">
                                                        <IconButton size="small" onClick={() => handleOpenChatDialog(assignment)}>
                                                            <Badge badgeContent={unreadCounts[assignment.id] || 0} color="error">
                                                                <Chat />
                                                            </Badge>
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Update Status">
                                                        <IconButton size="small" onClick={() => handleOpenStatusDialog(assignment)}>
                                                            <Edit />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="View Details">
                                                        <IconButton size="small" onClick={() => handleOpenViewDialog(assignment)}>
                                                            <Visibility />
                                                        </IconButton>
                                                    </Tooltip>
                                                </CardActions>
                                            </Card>
                                        </Zoom>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                </motion.div>

                {/* Update Status Dialog */}
                <Dialog
                    open={openStatusDialog}
                    onClose={handleCloseStatusDialog}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        Update Assignment Status
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            <FormControl fullWidth sx={{ mb: 2 }} size={isMobile ? "small" : "medium"}>
                                <InputLabel>Status</InputLabel>
                                <Select 
                                    value={statusForm.status} 
                                    onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value })} 
                                    label="Status"
                                >
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="pending">Pending</MenuItem>
                                    <MenuItem value="completed">Completed</MenuItem>
                                    <MenuItem value="cancelled">Cancelled</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField 
                                fullWidth 
                                label="Notes (Optional)" 
                                multiline 
                                rows={3} 
                                value={statusForm.notes} 
                                onChange={(e) => setStatusForm({ ...statusForm, notes: e.target.value })}
                                size={isMobile ? "small" : "medium"}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button onClick={handleCloseStatusDialog} size={isMobile ? "small" : "medium"}>Cancel</Button>
                        <Button onClick={handleUpdateStatus} variant="contained" disabled={actionLoading} size={isMobile ? "small" : "medium"}>
                            {actionLoading ? <CircularProgress size={20} /> : 'Update'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* View Details Dialog */}
                <Dialog
                    open={openViewDialog}
                    onClose={handleCloseViewDialog}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' },
                            maxHeight: '90vh',
                            borderRadius: 3
                        }
                    }}
                >
                    <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, borderBottom: `1px solid ${theme.palette.divider}` }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Person color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                    Assignment Details
                                </Typography>
                            </Box>
                            <Chip 
                                label={getStatusLabel(selectedAssignment?.status)} 
                                color={getStatusColor(selectedAssignment?.status)}
                                size="small"
                            />
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        {selectedAssignment && selectedAssignment.student && (
                            <Box sx={{ pt: 2 }}>
                                {/* Profile Header */}
                                <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 2, 
                                    mb: 3,
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    textAlign: { xs: 'center', sm: 'left' }
                                }}>
                                    <Avatar 
                                        src={getProfileImageUrl(selectedAssignment.student)}
                                        sx={{ 
                                            width: { xs: 80, sm: 100 }, 
                                            height: { xs: 80, sm: 100 },
                                            bgcolor: !getProfileImageUrl(selectedAssignment.student) ? stringToColor(selectedAssignment.student.name) : 'transparent',
                                            fontSize: '2.5rem',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        {!getProfileImageUrl(selectedAssignment.student) && getInitials(selectedAssignment.student.name)}
                                    </Avatar>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                            {selectedAssignment.student.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Email sx={{ fontSize: 14 }} />
                                            {selectedAssignment.student.email}
                                        </Typography>
                                        <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                            <School fontSize="small" />
                                            {selectedAssignment.student.university || 'University Not Assigned'}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Divider sx={{ my: 2 }} />

                                <Grid container spacing={3}>
                                    {/* Personal Information */}
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle1" sx={{ 
                                            fontWeight: 'bold', 
                                            color: theme.palette.primary.main, 
                                            mb: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1
                                        }}>
                                            <Person fontSize="small" />
                                            Personal Information
                                        </Typography>
                                        
                                        <Stack spacing={1.5}>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Badge fontSize="small" color="action" />
                                                <strong>Registration ID:</strong> {selectedAssignment.student.registration_id}
                                            </Typography>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Phone fontSize="small" color="action" />
                                                <strong>Phone:</strong> {selectedAssignment.student.contact_phone || 'Not provided'}
                                            </Typography>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Flag fontSize="small" color="action" />
                                                <strong>Nationality:</strong> {selectedAssignment.student.nationality || 'Not provided'}
                                            </Typography>
                                        </Stack>
                                    </Grid>

                                    {/* Assignment Information */}
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle1" sx={{ 
                                            fontWeight: 'bold', 
                                            color: theme.palette.primary.main, 
                                            mb: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1
                                        }}>
                                            <Assignment fontSize="small" />
                                            Assignment Information
                                        </Typography>
                                        <Stack spacing={1.5}>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Badge fontSize="small" color="action" />
                                                <strong>Assignment ID:</strong> {selectedAssignment.assignment_id}
                                            </Typography>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <CalendarToday fontSize="small" color="action" />
                                                <strong>Assigned Date:</strong> {formatCreatedAtDate(selectedAssignment.assigned_date)}
                                            </Typography>
                                            {selectedAssignment.completion_date && (
                                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <CheckCircle fontSize="small" color="action" />
                                                    <strong>Completion Date:</strong> {formatCreatedAtDate(selectedAssignment.completion_date)}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </Grid>

                                    {/* Assignment Notes */}
                                    {selectedAssignment.assignment_notes && (
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle1" sx={{ 
                                                fontWeight: 'bold', 
                                                color: theme.palette.primary.main, 
                                                mb: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <Feedback fontSize="small" />
                                                Assignment Notes
                                            </Typography>
                                            <Paper sx={{ p: 2, bgcolor: theme.palette.grey[50], borderRadius: 2 }}>
                                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                                                    {selectedAssignment.assignment_notes}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button onClick={handleCloseViewDialog} variant="contained" size={isMobile ? "small" : "medium"}>
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Chat Dialog - WhatsApp Style */}
                <Dialog
                    open={openChatDialog}
                    onClose={handleCloseChatDialog}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' },
                            height: '80vh',
                            maxHeight: '80vh',
                            borderRadius: 3,
                            display: 'flex',
                            flexDirection: 'column'
                        }
                    }}
                >
                    <DialogTitle sx={{ 
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        p: 2,
                        background: theme.palette.primary.main,
                        color: 'white'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar 
                                src={getProfileImageUrl(selectedAssignment?.student)}
                                sx={{ width: 40, height: 40 }}
                            >
                                {getInitials(selectedAssignment?.student?.name)}
                            </Avatar>
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                    {selectedAssignment?.student?.name}
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                    {selectedAssignment?.student?.email}
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton
                            onClick={handleCloseChatDialog}
                            sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }}
                        >
                            <Close />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent sx={{ p: 2, flex: 1, overflowY: 'auto', bgcolor: '#f5f5f5' }} id="chat-messages">
                        {messages.length === 0 ? (
                            <Box sx={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                height: '100%',
                                color: 'text.secondary'
                            }}>
                                <Chat sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                                <Typography variant="body2">No messages yet</Typography>
                                <Typography variant="caption">Start a conversation with {selectedAssignment?.student?.name}</Typography>
                            </Box>
                        ) : (
                            <Stack spacing={1.5}>
                                {messages.map((message, index) => {
                                    const isOwnMessage = message.sender_type === 'supervisor';
                                    const showDate = index === 0 || 
                                        new Date(message.created_at).toDateString() !== new Date(messages[index - 1]?.created_at).toDateString();
                                    
                                    return (
                                        <React.Fragment key={message.id}>
                                            {showDate && (
                                                <Box sx={{ textAlign: 'center', my: 2 }}>
                                                    <Chip 
                                                        label={new Date(message.created_at).toLocaleDateString(undefined, { 
                                                            weekday: 'long', 
                                                            year: 'numeric', 
                                                            month: 'long', 
                                                            day: 'numeric' 
                                                        })}
                                                        size="small"
                                                        sx={{ bgcolor: 'rgba(0,0,0,0.1)' }}
                                                    />
                                                </Box>
                                            )}
                                            <Box sx={{
                                                display: 'flex',
                                                justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
                                                mb: 0.5
                                            }}>
                                                <Paper sx={{
                                                    maxWidth: '70%',
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    bgcolor: isOwnMessage ? theme.palette.primary.main : 'white',
                                                    color: isOwnMessage ? 'white' : 'text.primary',
                                                    position: 'relative'
                                                }}>
                                                    <Typography variant="body2" sx={{ wordWrap: 'break-word' }}>
                                                        {message.message}
                                                    </Typography>
                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                        mt: 0.5,
                                                        justifyContent: 'flex-end'
                                                    }}>
                                                        <Typography variant="caption" sx={{
                                                            fontSize: '0.65rem',
                                                            color: isOwnMessage ? 'rgba(255,255,255,0.7)' : 'text.secondary'
                                                        }}>
                                                            {message.formatted_time}
                                                        </Typography>
                                                        {isOwnMessage && (
                                                            message.is_read ? (
                                                                <DoneAll sx={{ fontSize: 12, color: '#4fc3f7' }} />
                                                            ) : (
                                                                <Done sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }} />
                                                            )
                                                        )}
                                                    </Box>
                                                </Paper>
                                            </Box>
                                        </React.Fragment>
                                    );
                                })}
                            </Stack>
                        )}
                    </DialogContent>

                    <DialogActions sx={{ 
                        p: 2, 
                        borderTop: `1px solid ${theme.palette.divider}`,
                        bgcolor: 'white'
                    }}>
                        <TextField
                            fullWidth
                            placeholder="Type a message..."
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSendMessage();
                                }
                            }}
                            multiline
                            maxRows={3}
                            size="small"
                            InputProps={{
                                sx: { borderRadius: 3 }
                            }}
                        />
                        <IconButton 
                            color="primary" 
                            onClick={handleSendMessage}
                            disabled={!newMessage.trim() || sendingMessage}
                            sx={{ 
                                bgcolor: theme.palette.primary.main,
                                color: 'white',
                                '&:hover': { bgcolor: theme.palette.primary.dark },
                                '&.Mui-disabled': { bgcolor: theme.palette.grey[300] }
                            }}
                        >
                            {sendingMessage ? <CircularProgress size={24} /> : <Send />}
                        </IconButton>
                    </DialogActions>
                </Dialog>
            </FullWidthContainer>
        </>
    );
};

export default AssignedStudents;